import React, { useCallback, useMemo } from 'react';
import { CellContext } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { MembershipDto } from '@area-restrita/api-client/model/membershipDto';
import { useUpdateMembership } from '@area-restrita/api-client/endpoints/memberships/memberships.gen';

import { SelectOption } from '@/components/Select';
import RoleSelector from '@/components/QuerySelectors/RoleSelector';
import MutationStatusIndicator from '@/components/MutationStatusIndicator';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

type IProps = CellContext<MembershipDto, unknown>;

const CustomerRolesCell: React.FC<IProps> = ({ row }) => {
  const { roles: userRoles, user } = row.original;
  const checkAuthorization = useAuthSessionStore(
    (state) => state.checkAuthorization,
  );

  const queryClient = useQueryClient();
  const updateMembership = useUpdateMembership({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['/api/v1/memberships'] }),
    },
  });

  const value = useMemo(
    () =>
      userRoles.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    [userRoles],
  );

  const handleChange = useCallback(
    (value: SelectOption[]) =>
      updateMembership.mutateAsync({
        userId: user.id,
        data: {
          roles: value.map(({ value }) => value as number),
        },
      }),
    [updateMembership, user.id],
  );

  return (
    <RoleSelector
      multiple
      value={value}
      icon={<MutationStatusIndicator mutation={updateMembership} />}
      onChange={handleChange}
      disableOnSingle
      readOnly={checkAuthorization(({ cannot }) =>
        cannot('update', 'memberships'),
      )}
    />
  );
};

export default CustomerRolesCell;
